import React from 'react'
import { ColDef, RowNode } from 'ag-grid-community'
import { Avatar, IconButton, Tooltip } from '@mui/material'
import { styled } from '@mui/system'
import TenantStorage from '../../../../../../../../utils/storage'
import FileUploadDialog from '../../../../../../../components/dialogs/FileUploadDialog'
import ImageCropDialog from '../../../../../../../components/dialogs/ImageCropDialog'
import AlertDialog from '../../../../../../../components/dialogs/AlertDialog'
import HighlightOffIcon from '@mui/icons-material/HighlightOffRounded'
import { FunctionProperty } from '../../../../../../../../lib/commons/appFunction'
import { ICON_SIZE } from '../../../../../../BulkSheet/const'
import { intl } from '../../../../../../../../i18n'

const RootDiv = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
})

interface State {
  openSelectDialog: boolean
  openCropDialog: boolean
  cropSrc: string
  openMessage: boolean
  openAlertDialog: boolean
}

interface Props {
  colDef: ColDef
  uiMeta: FunctionProperty
  node: RowNode
  eGridCell: HTMLElement
  value?: string
}

class CellRenderer extends React.PureComponent<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      openSelectDialog: false,
      openCropDialog: false,
      openAlertDialog: false,
      cropSrc: '',
      openMessage: false,
    }
  }

  private openSelectDialog = () => {
    if (!this.props.colDef.editable) {
      return
    }
    this.setState({ openSelectDialog: true })
  }

  private closeSelectDialog = () => {
    this.setState({ openSelectDialog: false })
  }

  private closeCropDialog = () => {
    this.setState({ openCropDialog: false })
  }

  private openAlertDialog = () => {
    this.setState({ openAlertDialog: true })
  }

  private closeAlertDialog = () => {
    this.setState({ openAlertDialog: false })
  }

  private chooseFile = acceptedFiles => {
    this.setState({
      openCropDialog: true,
      cropSrc: URL.createObjectURL(acceptedFiles[0]),
    })
  }

  private uploadFile = async croppedImage => {
    const url = await TenantStorage.uploadFile(croppedImage).then(
      response => response.json.downloadUrl
    )
    this.onChange(url)
    this.setState({
      openSelectDialog: false,
      openCropDialog: false,
      cropSrc: '',
    })
  }

  private onClickDelete = () => {
    this.onChange('')
    this.closeAlertDialog()
  }

  private onChange = (url?: string) => {
    const fieldName = this.props.colDef.field!
    return this.props.node.setDataValue(fieldName, url)
  }

  render() {
    const { value, uiMeta, eGridCell } = this.props
    const iconSize = ICON_SIZE
    return (
      <RootDiv>
        <Tooltip
          title={intl.formatMessage({ id: 'iconCell.iconChange' })}
          placement="right-start"
        >
          <Avatar
            alt=""
            src={value}
            onClick={this.openSelectDialog}
            variant="rounded"
            style={{
              height: iconSize,
              width: iconSize,
            }}
          />
        </Tooltip>
        <ImageCropDialog
          open={this.state.openCropDialog}
          title={intl.formatMessage({ id: 'iconCell.title.setIconArea' })}
          url={this.state.cropSrc}
          closeHandler={this.closeCropDialog}
          submitHandler={this.uploadFile}
        />
        <FileUploadDialog
          open={this.state.openSelectDialog}
          title={intl.formatMessage({ id: 'iconCell.title.selectIcon' })}
          message={intl.formatMessage({
            id: 'iconCell.message.dragDropIcon',
          })}
          dropAcceptedHandler={this.chooseFile}
          multiple={false}
          accept={uiMeta.fileFormat}
          closeHandler={this.closeSelectDialog}
        />
        <AlertDialog
          isOpen={this.state.openAlertDialog}
          message={intl.formatMessage({ id: 'iconCell.message.deleteIcon' })}
          submitButtonTitle={intl.formatMessage({ id: 'dialog.delete' })}
          submitHandler={this.onClickDelete}
          closeButtonTitle={intl.formatMessage({ id: 'dialog.cancel' })}
          closeHandler={this.closeAlertDialog}
        />
        {value && (
          <Tooltip
            title={intl.formatMessage({ id: 'iconCell.iconDelete' })}
            placement="right-start"
          >
            <IconButton onClick={this.openAlertDialog}>
              <HighlightOffIcon color="action" />
            </IconButton>
          </Tooltip>
        )}
      </RootDiv>
    )
  }
}

export default CellRenderer
